import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Fish, LogOut, Trash2, Check, Users, Mail, Euro, Boxes, CalendarDays, Clock } from "lucide-react";
import { BarChart, Bar, XAxis, ResponsiveContainer, Cell } from "recharts";
import { useLang } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const APP_COLORS = { gestionale: "#00E5FF", team: "#80F2FF", kitchen: "#FF5A36", bar: "#FF8A70" };

export default function Admin() {
  const { t, lang, setLanguage } = useLang();
  const [pin, setPin] = useState("");
  const [authed, setAuthed] = useState(false);
  const [leads, setLeads] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState("");

  const headers = useCallback((p) => ({ headers: { "x-admin-pin": p || pin } }), [pin]);

  const load = useCallback(async (p) => {
    const [l, s, bk] = await Promise.all([
      axios.get(`${API}/leads`, { headers: { "x-admin-pin": p } }),
      axios.get(`${API}/leads/stats`, { headers: { "x-admin-pin": p } }),
      axios.get(`${API}/bookings`, { headers: { "x-admin-pin": p } }),
    ]);
    setLeads(l.data);
    setStats(s.data);
    setBookings(bk.data);
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await axios.post(`${API}/admin/verify`, {}, { headers: { "x-admin-pin": pin } });
      await load(pin);
      setAuthed(true);
    } catch {
      setErr(t.admin.wrongPin);
    }
  };

  const setStatus = async (id, status) => {
    await axios.patch(`${API}/leads/${id}`, { status }, headers());
    await load(pin);
    toast.success("OK");
  };

  const remove = async (id) => {
    await axios.delete(`${API}/leads/${id}`, headers());
    await load(pin);
  };

  const removeBooking = async (id) => {
    await axios.delete(`${API}/bookings/${id}`, headers());
    await load(pin);
  };

  const fmtDate = (d) => new Date(d).toLocaleDateString(lang === "de" ? "de-DE" : "it-IT", { day: "2-digit", month: "short", year: "numeric" });
  const euro = (n) => "€" + Number(n).toLocaleString(lang === "de" ? "de-DE" : "it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const statusColor = { new: "text-cyan-brand bg-cyan-brand/10", contacted: "text-coral-soft bg-coral-brand/10", won: "text-emerald-400 bg-emerald-400/10" };
  const statusLabel = { new: t.admin.statusNew, contacted: t.admin.statusContacted, won: t.admin.statusWon };

  if (!authed) {
    return (
      <div className="grain min-h-screen bg-ink-bg grid place-items-center px-5">
        <div className="aurora absolute inset-0 opacity-50" />
        <form onSubmit={login} data-testid="admin-login" className="relative w-full max-w-sm rounded-3xl border border-white/10 bg-ink-surface/70 backdrop-blur-2xl p-8 glow-cyan">
          <div className="flex items-center gap-2.5 mb-6">
            <span className="grid place-items-center w-9 h-9 rounded-full border border-cyan-brand/40 text-cyan-brand"><Fish size={18} /></span>
            <span className="font-display font-extrabold tracking-tight text-lg text-white">SOR<span className="text-cyan-brand">.</span></span>
          </div>
          <h1 className="font-display text-2xl font-bold text-white">{t.admin.title}</h1>
          <p className="text-white/55 text-sm mt-1 mb-6">{t.admin.pinPrompt}</p>
          <input
            type="password"
            inputMode="numeric"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            placeholder={t.admin.pin}
            data-testid="admin-pin-input"
            className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-white text-center tracking-[0.5em] focus:border-cyan-brand focus:outline-none transition-colors duration-200"
          />
          {err && <p className="text-coral-brand text-sm mt-3" data-testid="admin-error">{err}</p>}
          <button type="submit" data-testid="admin-login-btn" className="mt-5 w-full rounded-full bg-cyan-brand text-ink-bg font-semibold py-3 hover:bg-cyan-soft transition-colors duration-200 active:scale-95">
            {t.admin.enter}
          </button>
          <Link to="/" className="block text-center mt-4 font-mono text-xs text-white/40 hover:text-white transition-colors duration-200">← www.sor.de</Link>
        </form>
      </div>
    );
  }

  const chartData = stats ? Object.entries(stats.app_counts).map(([id, v]) => ({ name: t.appNames[id], id, value: v })) : [];

  const cards = [
    { label: t.admin.totalLeads, value: stats?.total ?? 0, icon: Users, color: "#00E5FF" },
    { label: t.admin.newLeads, value: stats?.new ?? 0, icon: Mail, color: "#80F2FF" },
    { label: t.admin.potentialRevenue, value: euro(stats?.revenue ?? 0), icon: Euro, color: "#FF5A36" },
    { label: t.admin.totalBookings, value: stats?.bookings ?? 0, icon: CalendarDays, color: "#FF8A70" },
  ];

  return (
    <div className="grain min-h-screen bg-ink-bg text-white">
      <header className="border-b border-white/10 backdrop-blur-2xl bg-ink-bg/70 sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <span className="grid place-items-center w-8 h-8 rounded-full border border-cyan-brand/40 text-cyan-brand"><Fish size={16} /></span>
            <span className="font-display font-extrabold tracking-tight text-white">SOR<span className="text-cyan-brand">.</span></span>
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 ml-2">{t.admin.dashboard}</span>
          </Link>
          <div className="flex items-center gap-3">
            <div className="flex items-center rounded-full border border-white/10 p-0.5 font-mono text-[11px]">
              {["it", "de"].map((l) => (
                <button key={l} onClick={() => setLanguage(l)} className={`px-2.5 py-1 rounded-full uppercase ${lang === l ? "bg-cyan-brand text-ink-bg" : "text-white/55"}`}>{l}</button>
              ))}
            </div>
            <button onClick={() => { setAuthed(false); setPin(""); }} data-testid="admin-logout" className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm hover:border-coral-brand hover:text-coral-brand transition-colors duration-200">
              <LogOut size={15} /> {t.admin.logout}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-5 md:px-8 py-8">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {cards.map((c) => (
            <div key={c.label} className="rounded-2xl border border-white/10 bg-ink-surface/50 p-6" data-testid={`stat-${c.label}`}>
              <span className="grid place-items-center w-10 h-10 rounded-xl mb-4" style={{ background: `${c.color}18`, color: c.color }}><c.icon size={18} /></span>
              <p className="font-display text-3xl font-bold">{c.value}</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/45 mt-1">{c.label}</p>
            </div>
          ))}
          <div className="rounded-2xl border border-white/10 bg-ink-surface/50 p-6 sm:col-span-2">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/45 mb-2 flex items-center gap-2"><Boxes size={14} /> {t.admin.appInterest}</p>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={70}>
                <BarChart data={chartData}>
                  <XAxis dataKey="name" tick={{ fill: "#5C6B8A", fontSize: 9 }} axisLine={false} tickLine={false} />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {chartData.map((d) => <Cell key={d.id} fill={APP_COLORS[d.id]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-white/30 text-sm">—</p>}
          </div>
        </div>

        <div className="mt-8 rounded-2xl border border-white/10 bg-ink-surface/50 overflow-hidden">
          <div className="px-6 py-4 border-b border-white/10">
            <h2 className="font-display text-xl font-bold">{t.admin.leadsList}</h2>
          </div>
          {leads.length === 0 ? (
            <p className="p-10 text-center text-white/40" data-testid="admin-no-leads">{t.admin.noLeads}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="leads-table">
                <thead>
                  <tr className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/40 border-b border-white/10">
                    <th className="text-left font-medium px-6 py-3">{t.admin.colName}</th>
                    <th className="text-left font-medium px-4 py-3">{t.admin.colContact}</th>
                    <th className="text-left font-medium px-4 py-3">{t.admin.colRestaurant}</th>
                    <th className="text-left font-medium px-4 py-3">{t.admin.colApps}</th>
                    <th className="text-right font-medium px-4 py-3">{t.admin.colEstimate}</th>
                    <th className="text-left font-medium px-4 py-3">{t.admin.colStatus}</th>
                    <th className="text-left font-medium px-4 py-3">{t.admin.colDate}</th>
                    <th className="text-right font-medium px-6 py-3">{t.admin.colActions}</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l) => (
                    <tr key={l.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors duration-150" data-testid={`lead-row-${l.id}`}>
                      <td className="px-6 py-4 font-medium text-white">{l.name}</td>
                      <td className="px-4 py-4 text-white/60"><div>{l.email}</div><div className="text-white/40 text-xs">{l.phone}</div></td>
                      <td className="px-4 py-4 text-white/60">{l.restaurant || "—"}</td>
                      <td className="px-4 py-4">
                        <div className="flex flex-wrap gap-1">
                          {l.apps.map((a) => <span key={a} className="font-mono text-[10px] px-2 py-0.5 rounded-full" style={{ background: `${APP_COLORS[a]}18`, color: APP_COLORS[a] }}>{t.appNames[a]}</span>)}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-right font-mono text-white/70">{euro(l.estimate)}</td>
                      <td className="px-4 py-4"><span className={`font-mono text-[10px] uppercase px-2 py-1 rounded-full ${statusColor[l.status] || statusColor.new}`}>{statusLabel[l.status] || l.status}</span></td>
                      <td className="px-4 py-4 text-white/50 whitespace-nowrap">{fmtDate(l.created_at)}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => setStatus(l.id, "contacted")} title={t.admin.markContacted} className="p-1.5 rounded-lg hover:bg-coral-brand/20 text-coral-soft transition-colors duration-150" data-testid={`mark-contacted-${l.id}`}><Mail size={14} /></button>
                          <button onClick={() => setStatus(l.id, "won")} title={t.admin.markWon} className="p-1.5 rounded-lg hover:bg-emerald-400/20 text-emerald-400 transition-colors duration-150" data-testid={`mark-won-${l.id}`}><Check size={14} /></button>
                          <button onClick={() => remove(l.id)} title={t.admin.delete} className="p-1.5 rounded-lg hover:bg-red-500/20 text-red-400 transition-colors duration-150" data-testid={`delete-${l.id}`}><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-8 rounded-2xl border border-white/10 bg-ink-surface/50 overflow-hidden">
          <div className="px-6 py-4 border-b border-white/10 flex items-center gap-2">
            <CalendarDays size={16} className="text-coral-soft" />
            <h2 className="font-display text-xl font-bold">{t.admin.bookingsList}</h2>
          </div>
          {bookings.length === 0 ? (
            <p className="p-10 text-center text-white/40" data-testid="admin-no-bookings">{t.admin.noBookings}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="bookings-table">
                <thead>
                  <tr className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/40 border-b border-white/10">
                    <th className="text-left font-medium px-6 py-3">{t.admin.colName}</th>
                    <th className="text-left font-medium px-4 py-3">{t.admin.colContact}</th>
                    <th className="text-left font-medium px-4 py-3">{t.admin.colRestaurant}</th>
                    <th className="text-left font-medium px-4 py-3">{t.admin.colDate}</th>
                    <th className="text-left font-medium px-4 py-3">{t.admin.colTime}</th>
                    <th className="text-right font-medium px-6 py-3">{t.admin.colActions}</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((bk) => (
                    <tr key={bk.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors duration-150" data-testid={`booking-row-${bk.id}`}>
                      <td className="px-6 py-4 font-medium text-white">{bk.name}</td>
                      <td className="px-4 py-4 text-white/60"><div>{bk.email}</div><div className="text-white/40 text-xs">{bk.phone}</div></td>
                      <td className="px-4 py-4 text-white/60">{bk.restaurant || "—"}</td>
                      <td className="px-4 py-4 text-white/70 whitespace-nowrap font-mono">{bk.date}</td>
                      <td className="px-4 py-4"><span className="inline-flex items-center gap-1.5 font-mono text-cyan-brand"><Clock size={13} /> {bk.time}</span></td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end">
                          <button onClick={() => removeBooking(bk.id)} title={t.admin.delete} className="p-1.5 rounded-lg hover:bg-red-500/20 text-red-400 transition-colors duration-150" data-testid={`delete-booking-${bk.id}`}><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
